import { motion } from "framer-motion";
import { Leaf, Dumbbell, Heart, Droplets, Brain, Zap } from "lucide-react";

const PRODUCT_IMAGE = "https://customer-assets.emergentagent.com/job_1372e601-3756-4090-a4b3-dfd0e81eaf5a/artifacts/6npxuyh5_MUSCLE%20MINERAL%20CREATINE%20MONO%20-FRONT.png";
const BUY_URL = "https://xtremenutrition.co.za/product/muscle-mineral-creatine-monohydrate-1kg/";

const benefitIcons = [
  { icon: Dumbbell, label: "Muscle Performance" },
  { icon: Heart, label: "Muscle Recovery" },
  { icon: Zap, label: "Muscle Growth" },
  { icon: Droplets, label: "Cellular Hydration" },
  { icon: Brain, label: "Cognitive Function" },
  { icon: Leaf, label: "Vegan Friendly" },
];

export const HeroSection = () => {
  return (
    <section 
      className="relative min-h-screen flex items-center overflow-hidden hero-bg"
      data-testid="hero-section"
    >
      {/* Background overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/95 to-[#050505]/70" />
      
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Content */}
          <div className="order-2 lg:order-1">
            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-sm tracking-[0.3em] text-[#E82D00] mb-4"
              data-testid="hero-tagline"
            >
              PURE · MICRONISED · UNFLAVOURED
            </motion.p>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-[#616556] mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Pure Micronised Creatine
            </motion.p>

            {/* Main Title */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold uppercase tracking-tighter text-white mb-6"
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid="hero-title"
            >
              Creatine<br />
              <span className="text-[#A3A3A3]">Monohydrate</span>
            </motion.h1>

            {/* Supporting line */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg md:text-xl text-[#E5E5E5] mb-4"
              style={{ fontFamily: 'Inter, sans-serif' }}
              data-testid="hero-supporting"
            >
              Performance Creatine for Athletes
            </motion.p>

            {/* Product Size */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="font-mono text-sm tracking-wider text-[#616556] mb-8"
            >
              1000g | 200 SERVINGS
            </motion.p>

            {/* Benefit Icons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-4 mb-10"
              data-testid="hero-benefits"
            >
              {benefitIcons.map((item, index) => (
                <div 
                  key={item.label}
                  className="flex items-center gap-2 text-[#A3A3A3]"
                  data-testid={`benefit-icon-${index}`}
                >
                  <div className="w-8 h-8 flex items-center justify-center border border-[#616556]/50 bg-[#616556]/10">
                    <item.icon size={16} strokeWidth={1.5} />
                  </div>
                  <span className="text-xs font-mono tracking-wider hidden sm:block">{item.label}</span>
                </div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href="#product"
                className="bg-transparent border border-white/20 hover:border-white text-white font-bold uppercase tracking-wider px-8 py-4 text-center transition-colors duration-300"
                style={{ fontFamily: 'Oswald, sans-serif' }}
                data-testid="hero-learn-btn"
              >
                Learn More
              </a>
            </motion.div>
          </div>

          {/* Right: Product Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="order-1 lg:order-2 flex justify-center lg:justify-end"
            data-testid="hero-product-container"
          >
            <div className="relative">
              {/* Glow effect behind product */}
              <div className="absolute inset-0 bg-[#E82D00]/10 blur-[100px] rounded-full" />
              
              <motion.img
                src={PRODUCT_IMAGE}
                alt="Muscle Mineral Creatine Monohydrate"
                className="relative z-10 w-[280px] sm:w-[350px] lg:w-[450px] h-auto product-glow animate-float"
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                data-testid="hero-product-image"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border border-white/30 rounded-full flex justify-center pt-2"
        >
          <div className="w-1 h-2 bg-white/50 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
